import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Map, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import api from '../api/client';

export function LoginPage() {
  const { login, loadUser } = useAuth();
  const navigate = useNavigate();

  // Step: 'credentials' | 'otp'
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // OTP state
  const [otpToken, setOtpToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Step 1: Credentials
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });

      if (data.requires_otp) {
        setOtpToken(data.otp_token);
        setMaskedEmail(data.masked_email);
        setStep('otp');
        setCountdown(60);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        // 2FA devre dışı — direkt giriş
        localStorage.setItem('token', data.token);
        await loadUser();
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Giriş yapılamadı');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: OTP verify
  const handleVerifyOtp = useCallback(async (code) => {
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/verify-otp', {
        otp_token: otpToken,
        code,
      });
      localStorage.setItem('token', data.token);
      await loadUser();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Doğrulama başarısız');
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setLoading(false);
    }
  }, [otpToken, loadUser, navigate]);

  // OTP input handling
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5) {
      const code = next.join('');
      if (code.length === 6) handleVerifyOtp(code);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 0) return;
    const next = [...otp];
    for (let i = 0; i < 6; i++) next[i] = text[i] || '';
    setOtp(next);
    if (text.length === 6) handleVerifyOtp(text);
    else inputRefs.current[text.length]?.focus();
  };

  // Resend
  const handleResend = async () => {
    if (countdown > 0) return;
    setError('');
    try {
      const { data } = await api.post('/auth/resend-otp', { otp_token: otpToken });
      setOtpToken(data.otp_token);
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Kod gönderilemedi');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-600 rounded-2xl mb-3 shadow-lg">
            <Map size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Fieldpin</h1>
          <p className="text-slate-400 text-sm mt-1">Yönetici Paneli</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {step === 'credentials' ? (
            <>
              <h2 className="text-lg font-semibold text-slate-800 mb-6">Giriş Yap</h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <Input label="E-posta" type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@fieldpin.io" required />
                <Input label="Şifre" type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required />
                <div className="flex justify-end -mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Şifremi unuttum?
                  </Link>
                </div>
                <Button type="submit" loading={loading} className="w-full justify-center mt-2">
                  Giriş Yap
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-5">
                Hesabınız yok mu?{' '}
                <Link to="/register" className="text-blue-600 font-medium hover:underline">Ücretsiz Dene</Link>
              </p>

            </>
          ) : (
            <>
              {/* OTP Step */}
              <button onClick={() => { setStep('credentials'); setError(''); setOtp(['','','','','','']); }}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
                <ArrowLeft size={14} /> Geri
              </button>

              <h2 className="text-lg font-semibold text-slate-800 mb-2">Doğrulama Kodu</h2>
              <p className="text-sm text-slate-500 mb-6">
                <span className="font-medium text-slate-700">{maskedEmail}</span> adresine 6 haneli kod gönderildi.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              {/* 6-digit OTP inputs */}
              <div className="flex justify-center gap-2 mb-6" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className={`w-11 h-13 text-center text-xl font-bold border-2 rounded-xl outline-none transition
                      ${digit ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}
                      focus:border-blue-500 focus:ring-2 focus:ring-blue-200`}
                    disabled={loading}
                  />
                ))}
              </div>

              <p className="text-xs text-slate-400 text-center mb-4">
                Kod 5 dakika geçerlidir
              </p>

              {/* Resend */}
              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-slate-400">
                    Tekrar göndermek için <span className="font-medium text-slate-600">{countdown}s</span> bekleyin
                  </p>
                ) : (
                  <button onClick={handleResend}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mx-auto">
                    <RefreshCw size={13} /> Kodu Tekrar Gönder
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
